/**
 * UNIDADES COM NOME E SOBRENOME
 *
 * Este arquivo existe por um motivo só, e é o motivo de o projeto usar
 * TypeScript: neste sistema quase todo número tem unidade, e trocar uma pela
 * outra é a falha mais provável e a mais silenciosa. Grama por quilo, saco por
 * quilo, hectare por metro quadrado, peso de um peixe por biomassa do tanque.
 * O número sai errado sem quebrar nada, e só aparece meses depois num
 * relatório que não fecha.
 *
 * A solução: um `number` comum não vira Gramas sozinho. Cada unidade é um tipo
 * distinto, e o compilador recusa misturar. Na prática:
 *
 *     const peso = g(850)            // Gramas
 *     const area = ha(1.62)          // Hectares
 *     peso + area                    // ERRO — o compilador não deixa
 *     gParaKg(peso)                  // Quilos — conversão explícita, sempre
 *
 * Em tempo de execução tudo continua sendo número puro: zero custo, zero peso
 * no celular. A marca só existe enquanto o código é conferido.
 *
 * REGRA: nenhum número com unidade entra ou sai deste sistema como `number`
 * cru. Se apareceu `number` numa conta de domínio, falta uma unidade.
 */

declare const marcaDeUnidade: unique symbol

type Marcado<U extends string> = number & { readonly [marcaDeUnidade]: U }

// ---------- peso ----------
export type Gramas = Marcado<'g'>
export type Quilos = Marcado<'kg'>
export type Toneladas = Marcado<'t'>

// ---------- área ----------
export type MetrosQuadrados = Marcado<'m2'>
export type Hectares = Marcado<'ha'>

// ---------- contagem e tempo ----------
export type Peixes = Marcado<'peixes'>
export type Dias = Marcado<'dias'>

// ---------- taxas ----------
/** Conversão alimentar: quilos de ração por quilo de peixe ganho. */
export type ConversaoAlimentar = Marcado<'CA'>
/** Carga da água: quilos de ração por hectare por dia. */
export type KgPorHaDia = Marcado<'kg/ha/dia'>
/** Toneladas de peixe por hectare. */
export type ToneladasPorHectare = Marcado<'t/ha'>
/** Fração de 0 a 1 — ociosidade, sobrevivência, percentual do peso vivo. */
export type Fracao = Marcado<'fração'>

// ---------------------------------------------------------------------------
// Construtores. É aqui — e só aqui — que um número cru ganha unidade.
// Ler como uma afirmação: `g(850)` é "estes 850 são gramas".
// ---------------------------------------------------------------------------

export const g = (n: number): Gramas => n as Gramas
export const kg = (n: number): Quilos => n as Quilos
export const t = (n: number): Toneladas => n as Toneladas
export const m2 = (n: number): MetrosQuadrados => n as MetrosQuadrados
export const ha = (n: number): Hectares => n as Hectares
export const peixes = (n: number): Peixes => n as Peixes
export const dias = (n: number): Dias => n as Dias
export const ca = (n: number): ConversaoAlimentar => n as ConversaoAlimentar
export const kgHaDia = (n: number): KgPorHaDia => n as KgPorHaDia
export const tHa = (n: number): ToneladasPorHectare => n as ToneladasPorHectare
export const fracao = (n: number): Fracao => n as Fracao

// ---------------------------------------------------------------------------
// Conversões. Explícitas de propósito: converter é uma decisão, não um acaso.
// ---------------------------------------------------------------------------

export const gParaKg = (v: Gramas): Quilos => kg(v / 1000)
export const kgParaG = (v: Quilos): Gramas => g(v * 1000)
export const kgParaT = (v: Quilos): Toneladas => t(v / 1000)
export const tParaKg = (v: Toneladas): Quilos => kg(v * 1000)

export const m2ParaHa = (v: MetrosQuadrados): Hectares => ha(v / 10_000)
export const haParaM2 = (v: Hectares): MetrosQuadrados => m2(v * 10_000)

/**
 * Sacaria para quilo. O peso do saco é parâmetro da fazenda — 25 kg é o padrão
 * nacional, mas fica em tabela, nunca fixo no código (regra 10 do CLAUDE.md).
 */
export const sacosParaKg = (sacos: number, pesoDoSacoKg: Quilos): Quilos =>
  kg(sacos * pesoDoSacoKg)

export const kgParaSacos = (quilos: Quilos, pesoDoSacoKg: Quilos): number =>
  quilos / pesoDoSacoKg

/**
 * Em sacaria só existe saco inteiro ou meio saco — o tratador conta sacos, não
 * pesa. A mesma regra está no CHECK da tabela `tratos`; aqui ela serve para a
 * tela recusar antes de tentar gravar.
 */
export const sacoValido = (sacos: number): boolean =>
  sacos > 0 && Number.isInteger(sacos * 2)

// ---------------------------------------------------------------------------
// Formatação para leitura humana. Sempre pt-BR: vírgula decimal, ponto de
// milhar. Número que o gestor não reconhece é número que ele não confere.
// ---------------------------------------------------------------------------

export const numero = (n: number, casas = 1): string =>
  n.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })

export const inteiro = (n: number): string => Math.round(n).toLocaleString('pt-BR')

export const emKg = (v: Quilos): string => `${numero(v, 1)} kg`
export const emG = (v: Gramas): string => `${inteiro(v)} g`
export const emPorcento = (v: Fracao, casas = 0): string => `${numero(v * 100, casas)}%`

// ---------------------------------------------------------------------------
// A FRONTEIRA: onde texto de campo vira número
//
// Todo campo de tela devolve texto, e texto pode ser qualquer coisa. O gestor
// digitou «2.9\» no oxigênio e o app respondeu «água boa — trato cheio»:
// `Number()` deu NaN, NaN não é maior nem menor que nada, e a ausência de
// faixa que penalizasse foi lida como ausência de problema.
//
// Valor impossível não pode virar permissão. São duas defesas e as duas
// precisam existir — uma impede o lixo de ser digitado, a outra impede o
// número absurdo de ser aceito.
//
// ── O PADRÃO DO SISTEMA, decidido pelo gestor em 20/09/2026 ────────────────
//
//   VÍRGULA separa a casa decimal.      19,2 kg
//   PONTO separa a casa de milhar.      1.200 peixes
//
// É o padrão brasileiro, e é o que está escrito nas fichas de papel da
// fazenda há oito anos. Antes desta decisão o ponto era aceito como decimal,
// e isso tinha um preço silencioso: «1.200» virava 1,2 — um tratador que
// contasse mil e duzentos peixes do jeito que ele escreve no caderno tinha a
// contagem reduzida a um peixe e pouco, sem aviso nenhum.
//
// **NÚMERO AMBÍGUO NÃO É ADIVINHADO — É RECUSADO.** «19.2» não vira 19,2 nem
// 192: não é número nenhum, e a tela diz para usar vírgula. Adivinhar aqui
// seria escolher por quem digitou, calado, num campo que vai virar peso de
// peixe. A tela ensinar o padrão custa uma digitação; adivinhar errado custa
// um ciclo inteiro ancorado no número errado.
// ---------------------------------------------------------------------------

/**
 * Deixa passar só algarismo, vírgula e ponto — e UMA vírgula só.
 *
 * A segunda vírgula é a mão molhada no teclado pequeno, e ela era cara: «2,,0»
 * na tara não virava número, virava tara ZERO, e o peso do cesto entrava
 * somado ao do peixe. O lixo agora não chega nem a existir no campo.
 */
/**
 * INTEIRO QUE VEIO DO SERVIDOR — a defesa contra o bigint que chega como texto.
 *
 * `count(*)` e `sum(integer)` voltam do Postgres como BIGINT, e o driver
 * entrega bigint como TEXTO para não perder precisão em números que o
 * JavaScript não representa. O tipo diz `number`, o JSON traz `"0"`, e o
 * compilador não pega: resposta de rede é afirmada, não verificada.
 *
 * Em JavaScript o estrago é silencioso e some no meio da conta:
 *
 *     "0" === 0      → falso
 *     "3" + 1        → "31"
 *     "8800" * 900   → 7.920.000   (a multiplicação funciona, e por isso
 *                                   metade da tela parece certa)
 *
 * Custou dois defeitos que pareciam assuntos diferentes: o contador que
 * mostrava 10, 20 e 30 tratos, e o rodapé que afirmava que todos os tanques
 * tinham falta registrada. Nas duas vezes o conserto foi no que aparecia.
 *
 * O conserto de verdade é em dois lugares, e os dois precisam existir: `::int`
 * na consulta, para o dado nascer certo, e esta função na fronteira, para a
 * próxima consulta que esquecer não derrubar nada.
 */
export function inteiroDoServidor(valor: unknown): number {
  const n = typeof valor === 'number' ? valor : Number(valor)
  return Number.isFinite(n) ? Math.round(n) : 0
}

export function soNumero(texto: string): string {
  const limpo = texto.replace(/[^\d,.]/g, '')
  const primeira = limpo.indexOf(',')
  if (primeira === -1) return limpo
  return limpo.slice(0, primeira + 1) + limpo.slice(primeira + 1).replace(/,/g, '')
}

/**
 * O padrão brasileiro, lido sem adivinhação: vírgula é decimal, ponto é
 * milhar, e o que não obedece devolve NULO.
 *
 * Aceita «19,2», «1.200», «1.200,5», «1200,5», «,5». Recusa «19.2» (ponto que
 * não separa milhar), «1.20», «19,2,3» e qualquer outra coisa.
 *
 * A vírgula ou o ponto no fim são tolerados porque a pessoa está no meio da
 * digitação: «19,» é 19 enquanto ela não termina. Sem isso a tela piscaria um
 * erro a cada tecla.
 */
export function numeroPtBr(texto: string): number | null {
  const t = texto.trim().replace(/[.,]$/, '')
  if (t === '') return null
  // Parte inteira: ou grupos de milhar bem formados, ou algarismos soltos.
  if (!/^(\d{1,3}(\.\d{3})+|\d*)(,\d+)?$/.test(t)) return null
  const v = Number(t.replace(/\./g, '').replace(',', '.'))
  return Number.isFinite(v) ? v : null
}

/**
 * Texto virando número, ou NULO quando não é número dentro do intervalo.
 *
 * Os limites acompanham o que o BANCO e o SERVIDOR aceitam, de propósito: se o
 * envio vai ser recusado, a tela tem de recusar antes — senão o registro só
 * descobre que é inválido lá na frente, no Sincronizar, longe de quem digitou
 * e longe do peixe que já voltou para a água.
 */
export function numeroNaFaixa(texto: string, de: number, ate: number): number | null {
  const v = numeroPtBr(texto)
  return v !== null && v >= de && v <= ate ? v : null
}

/**
 * O mesmo, mas para o que se CONTA. Peixe não tem metade.
 *
 * Existe porque `numeroNaFaixa` aceitava «12,5» peixes numa coluna que o banco
 * guarda como inteiro: o número exibido na tela e o número guardado deixavam
 * de ser o mesmo, e qualquer conta refeita depois devolvia outro peso médio.
 */
export function inteiroNaFaixa(texto: string, de: number, ate: number): number | null {
  const v = numeroNaFaixa(texto, de, ate)
  return v !== null && Number.isInteger(v) ? v : null
}

/** Há texto no campo, mas ele não é número nenhum. A tela usa para explicar. */
export function ilegivel(texto: string): boolean {
  return texto.trim() !== '' && numeroPtBr(texto) === null
}

/** «3½», «½», «2» — o jeito da fazenda escrever meio saco. */
export function emSacos(sacos: number): string {
  if (sacos === 0.5) return '½'
  if (Number.isInteger(sacos)) return String(sacos)
  return `${Math.floor(sacos)}½`
}

/** «20/09/2026» — a data no jeito do papel e da planilha, a partir do ISO. */
export function diaCurto(iso: string): string {
  const [a, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}/${a}`
}

/**
 * Dias inteiros entre duas datas ISO. Negativo se a segunda for anterior.
 *
 * Ao meio-dia UTC, e não à meia-noite: meia-noite é justamente a hora em que o
 * horário de verão de algum lugar pula, e uma data vira a outra.
 */
export function diasEntreDatas(de: string, ate: string): number {
  const a = new Date(`${de.slice(0, 10)}T12:00:00Z`).getTime()
  const b = new Date(`${ate.slice(0, 10)}T12:00:00Z`).getTime()
  if (Number.isNaN(a) || Number.isNaN(b)) return 0
  return Math.round((b - a) / 86_400_000)
}
